import React, { Component, createRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import ProductItem from '../Category/ProductItem';
import Pagination from '../../hocs/Pagination/Pagination';
import { retrieveProductCache } from '../../actions/checkout';
import { searchInput, goToPage } from '../../actions/environment';
import { translate, prefix } from '../../utils/locale';
import { urlify, splitNewLine } from '../../utils/string';
import { unique, intersect } from '../../utils/array';
import s from './Search.scss';

class Search extends Component {
	constructor(props) {
		super(props);

		this.debounceTimer = false;
		this.inputElement = createRef();
	}

	componentDidMount() {
		if (this.searchForm) this.searchForm.querySelector('input').focus();
	}

	searchHandler = (e) => {
		e.preventDefault();
	};

	triggerSearchEvent = (value) => {
		if (typeof window !== 'undefined') {
			if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
			if (value.length > 3) {
				window.dataLayer.push({
					event: 'Search',
					values: {
						language: this.props.setup.language,
						search_string: value.toLowerCase(),
					},
				});
			}
		}
	};

	onSearchInputChange = (e) => {
		e.preventDefault();
		const $input = e.currentTarget;
		const { value = '' } = $input;
		this.props.searchInput(value);
		clearTimeout(this.debounceTimer);
		this.debounceTimer = setTimeout(() => {
			this.triggerSearchEvent(value);
		}, 300);
		if (typeof window !== 'undefined') window.history.replaceState({}, '', prefix(this.props.setup, `search${value.length > 0 ? '?' : ''}${value.toLowerCase().replace(' ', '+')}`));
	};

	clearTextHandler = (e) => {
		e.preventDefault();
		const { searchInputText = '' } = this.props;
		if (searchInputText === '') {
			this.props.closeSearch();
		} else {
			this.props.searchInput('');
			this.inputElement.current.focus();
		}
	};

	addSuggestion = (e) => {
		e.preventDefault();
		const { searchInputText = '', setup } = this.props;
		let value = e.currentTarget.text.toLowerCase();
		if (urlify(value) === urlify(searchInputText)) {
			value = '';
			this.inputElement.current.focus();
		}
		this.props.searchInput(value);
		this.triggerSearchEvent(value);
		if (typeof window !== 'undefined') window.history.replaceState({}, '', prefix(setup, `search${value.length > 0 ? '?' : ''}${value.toLowerCase().replace(/\s/g, '+')}`));
	};

	render() {
		const { setup, searchInputText = '', productCache, closeSearch } = this.props;
		if (!productCache) return null;
		if (!productCache?.[setup.market]?.[setup.pricelist]) {
			this.props.retrieveProductCache();
			return null;
		}

		const suggestions = translate(setup, 'suggestionExamples');
		const filtering = productCache[setup.market][setup.pricelist];
		const searchTerms = filtering.searchTerms[setup.language];
		let productList = [];

		if (searchInputText !== '' && searchTerms) {
			searchInputText.split(' ').forEach((wordPart, i) => {
				if (wordPart === '') return;
				let currentSearchMatch = [];

				Object.keys(searchTerms).forEach((term) => {
					const wordPartIndex = term.toLowerCase().indexOf(wordPart.toLowerCase());
					let specificity = 1;


					if (wordPartIndex !== -1 && term && searchTerms[term]) {
						specificity = 1 / (searchTerms[term].length || 1); // More products per key = less specific
						specificity = specificity * 100 * (wordPartIndex === 0 ? 10 : 1);

						currentSearchMatch.push({ term, specificity, products: searchTerms[term] });
					}
				});

				if (currentSearchMatch.length === 0) return;

				currentSearchMatch.sort((a, b) => a.specificity - b.specificity);
				currentSearchMatch = currentSearchMatch.reduce((acc, product) => [...acc, ...product.products], []);
				currentSearchMatch = unique(currentSearchMatch);

				productList = i === 0 ? [...currentSearchMatch] : intersect(productList, currentSearchMatch);
			});

			if (typeof window !== 'undefined') window.history.replaceState({}, '', prefix(setup, `search?${searchInputText.toLowerCase().replace(' ', '+')}`));
		}

		productList = productList.sort((a, b) => {
			if (!filtering.products[a] || !filtering.products[b]) return -1;
			return filtering.products[a].added < filtering.products[b].added ? 1 : -1;
		});

		return (
			<div className={s.search}>
				<div className={s.search_fixed_content}>
					<form className={s.search_form} ref={(form) => { this.searchForm = form; }} onSubmit={this.searchHandler}>
						<div className={s.search_form_input}>
							<input id="search" ref={this.inputElement} className={s.search_form_input_field} type="text" value={searchInputText} onChange={this.onSearchInputChange} placeholder="Type your search here" />
							<span className={s.search_form_input_underline} aria-hidden="true">{searchInputText || ' '}</span>
							<a className={s.search_form_input_clear} href="#clearText" onClick={this.clearTextHandler}>
								<span className={s.search_form_input_clear_text}>{ translate(setup, 'clear') }</span>
								<svg width="10" height="10" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<line x1="0.141421" y1="0.858579" x2="12.1414" y2="12.8586" stroke="black" strokeWidth="0.5" />
									<line x1="12.1414" y1="1.14142" x2="0.141422" y2="13.1414" stroke="black" strokeWidth="0.5" />
								</svg>
							</a>
						</div>
						<button className={s.search_form_submit} type="submit" aria-label="Search" />
					</form>
					<div className={s.search_suggestions}>
						<span className={s.search_suggestions_title}>{ translate(setup, 'suggestions') }</span>
						<div className={s.search_suggestions_links}>
							{
								suggestions ? splitNewLine(suggestions).map((suggestion) => (
									<a key={urlify(suggestion)} className={[s.search_suggestions_link, (urlify(suggestion) === urlify(searchInputText) ? s.search_suggestions_link__selected : null)].join(' ')} onClick={this.addSuggestion} href="#suggestion"><span>{ suggestion.replace(/^\s*|\s*$/g, '') }</span></a>
								)) : null
							}
						</div>
					</div>
				</div>
				<div className={s.search_products}>
					{ productList.length > 0 && <Pagination ListComponent={ProductItem} list={productList} cache={filtering.products} currentPath="search" canonical closeSearch={closeSearch} /> }
				</div>
			</div>
		);
	}
}

const mapState = ({ environment, location, productCache, filtering }, props) => ({
	setup: environment.setup,
	searchInputText: environment.searchInputText,
	location,
	productCache,
	filtering,
	...props,
});

export default hot(module)(connect(mapState, { searchInput, goToPage, retrieveProductCache })(Search));
